if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        const activeSubmitButton = evt.submitter || this.submitButton;
        const isBuyNow = activeSubmitButton.name === 'checkout';
        const loadingSpinner = activeSubmitButton.querySelector('.loading__spinner');

        if (activeSubmitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        activeSubmitButton.setAttribute('aria-disabled', true);
        activeSubmitButton.classList.add('loading');
        loadingSpinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const suggestedAddOns = this.closest('product-info')?.querySelector('suggested-add-ons');
        const additionalProducts = this.closest('product-info')?.querySelector('additional-products');
        const setBuilder = this.closest('product-info')?.querySelector('set-builder');
        const setBuilderSubmission = setBuilder?.isSubmissionActive?.() === true;
        const existingOptionalProductItems = setBuilderSubmission
          ? null
          : suggestedAddOns?.getCartItems(formData) || additionalProducts?.getCartItems(formData);
        const setBuilderItems = setBuilder?.getCartItems(formData);
        const optionalProductItems = setBuilderSubmission
          ? setBuilderItems
          : existingOptionalProductItems || setBuilderItems
            ? [
                (existingOptionalProductItems || setBuilderItems)[0],
                ...(existingOptionalProductItems?.slice(1) || []),
                ...(setBuilderItems?.slice(1) || []),
              ]
            : null;
        if (this.cart && !isBuyNow) {
          this.cart.setActiveElement(document.activeElement);

          if (!optionalProductItems) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          }
        }
        if (optionalProductItems) {
          const requestBody = { items: optionalProductItems };

          if (this.cart && !isBuyNow) {
            requestBody.sections = this.cart.getSectionsToRender().map((section) => section.id);
            requestBody.sections_url = window.location.pathname;
          }

          config.headers['Content-Type'] = 'application/json';
          config.headers.Accept = 'application/json';
          config.body = JSON.stringify(requestBody);
        } else {
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = activeSubmitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              activeSubmitButton.setAttribute('aria-disabled', true);
              activeSubmitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (isBuyNow) {
              window.location = this.dataset.checkoutUrl;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            } else if (!response.sections) {
              // A third-party script (e.g. a back-in-stock/preorder app) can intercept
              // this fetch and strip the rendered `sections` payload. The item was still
              // added successfully, so fall back to the cart page instead of silently
              // failing to open the drawer/notification.
              console.warn('Add to cart: response was missing rendered sections, redirecting to the cart page.');
              window.location = window.routes.cart_url;
              return;
            }

            if (optionalProductItems && !response.key && response.items?.[0]?.key) {
              response.key = response.items[0].key;
            }

            this.updateCustomCartCount();

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error('Unable to add the product and selected optional products to the cart:', e);
          })
          .finally(() => {
            activeSubmitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) activeSubmitButton.removeAttribute('aria-disabled');
            loadingSpinner?.classList.add('hidden');
          });
      }

      async updateCustomCartCount() {
        try {
          const shopRoot = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${shopRoot}cart.js`, {
            headers: { Accept: 'application/json' },
          });

          if (!response.ok) {
            throw new Error(`Cart request failed with status ${response.status}`);
          }

          const cart = await response.json();
          const cartLinks = document.querySelectorAll('.gs-header a[aria-label="Cart"]');

          cartLinks.forEach((cartLink) => {
            let cartCount = cartLink.querySelector('.gs-cart-count');

            if (cart.item_count < 1) {
              cartCount?.remove();
              return;
            }

            if (!cartCount) {
              cartCount = document.createElement('span');
              cartCount.className = 'gs-cart-count';
              cartCount.setAttribute('aria-hidden', 'true');
              cartLink.appendChild(cartCount);
            }

            cartCount.textContent = String(cart.item_count);
          });
        } catch (error) {
          console.error('Unable to update the custom cart count:', error);
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
